import type * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import type {
  ComponentPropsWithoutRef,
  ComponentRef,
  HTMLAttributes,
  ReactNode,
} from "react";

import type { TIconKey } from "@/ui/components/primitives/Icon";
import type { DefinedDefaults } from "@/ui/lib";

/*-------------------------------------
|  Constants
|-------------------------------------*/

export const DROPDOWN_MENU_ITEM_INDICATOR_ICON: TIconKey = "tick";
export const DROPDOWN_MENU_SUB_TRIGGER_ICON: TIconKey = "chevronRight";

export const DROPDOWN_MENU_CONTENT_SIDE_OFFSET = 4;

export const DROPDOWN_MENU_ITEM_VARIANT_CLASS_MAP: Record<TDropdownMenuItemVariant, string> = {
  default: "focus:bg-component-select-item-focused focus:text-foreground-default",
  destructive: "text-state-error focus:bg-state-error focus:text-foreground-inverse",
};

/*-------------------------------------
|  Types
|-------------------------------------*/

export type TDropdownMenuItemVariant = "default" | "destructive";

export type TDropdownMenuRootProps = ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Root>;
export type TDropdownMenuProps = TDropdownMenuRootProps & {
  contentClassName?: string;
  label?: ReactNode;
  triggerClassName?: string;
} & Pick<HTMLAttributes<HTMLElement>, "children">;
export type TDropdownMenuTriggerElement = ComponentRef<typeof DropdownMenuPrimitive.Trigger>;
export type TDropdownMenuTriggerProps = ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Trigger>;
export type TDropdownMenuContentElement = ComponentRef<typeof DropdownMenuPrimitive.Content>;
export type TDropdownMenuContentProps = ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>;
export type TDropdownMenuGroupProps = ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Group>;
export type TDropdownMenuLabelElement = ComponentRef<typeof DropdownMenuPrimitive.Label>;
export type TDropdownMenuLabelProps = ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
  inset?: boolean;
};
export type TDropdownMenuItemElement = ComponentRef<typeof DropdownMenuPrimitive.Item>;
export type TDropdownMenuItemProps = ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
  icon?: TIconKey;
  inset?: boolean;
  variant?: TDropdownMenuItemVariant;
};
export type TDropdownMenuCheckboxItemElement = ComponentRef<typeof DropdownMenuPrimitive.CheckboxItem>;
export type TDropdownMenuCheckboxItemProps = ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem> & {
  icon?: TIconKey;
  variant?: TDropdownMenuItemVariant;
};
export type TDropdownMenuRadioGroupProps = ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioGroup>;
export type TDropdownMenuRadioItemElement = ComponentRef<typeof DropdownMenuPrimitive.RadioItem>;
export type TDropdownMenuRadioItemProps = ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem> & {
  icon?: TIconKey;
  variant?: TDropdownMenuItemVariant;
};
export type TDropdownMenuSeparatorElement = ComponentRef<typeof DropdownMenuPrimitive.Separator>;
export type TDropdownMenuSeparatorProps = ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>;
export type TDropdownMenuSubProps = ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Sub>;
export type TDropdownMenuSubTriggerElement = ComponentRef<typeof DropdownMenuPrimitive.SubTrigger>;
export type TDropdownMenuSubTriggerProps = ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & {
  icon?: TIconKey;
  inset?: boolean;
};
export type TDropdownMenuSubContentElement = ComponentRef<typeof DropdownMenuPrimitive.SubContent>;
export type TDropdownMenuSubContentProps = ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>;
export type TDropdownMenuShortcutProps = HTMLAttributes<HTMLSpanElement>;

type TDropdownMenuContentDefaultProps = DefinedDefaults<
  TDropdownMenuContentProps,
  "sideOffset" |
  "align"
>;

/*-------------------------------------
|  Defaults
|-------------------------------------*/

export const DROPDOWN_MENU_CONTENT_DEFAULTS = {
  sideOffset: DROPDOWN_MENU_CONTENT_SIDE_OFFSET,
  align: "start",
} satisfies TDropdownMenuContentDefaultProps;
